import React, { useState } from 'react';

// ── Design tokens ──────────────────────────────────────────────
const V = {
  bg:     '#0a0a0c',
  bg2:    '#111114',
  bg3:    '#18181c',
  gold:   '#c9a84c',
  goldL:  '#e8c97a',
  sage:   '#7aab8a',
  rose:   '#c47a7a',
  sky:    '#7a9ec4',
  lav:    '#9a7ac4',
  border: 'rgba(201,168,76,0.15)',
  text:   '#f0ede6',
  muted:  'rgba(240,237,230,0.5)',
  dim:    'rgba(240,237,230,0.22)',
};

const serif   = { fontFamily: "'Cormorant Garamond', serif" };
const jost    = { fontFamily: "'Jost', sans-serif" };
const eyebrow = { fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: V.gold, ...jost };
const card    = { background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '12px', padding: '22px 26px' };
const btnGold = {
  padding: '12px 28px', background: 'transparent',
  border: `1px solid ${V.gold}`, borderRadius: '8px',
  color: V.gold, fontSize: '14px', cursor: 'pointer', ...jost,
  letterSpacing: '0.04em',
};
const btnGhost = {
  padding: '8px 16px', background: 'transparent',
  border: `0.5px solid ${V.border}`, borderRadius: '6px',
  color: V.muted, fontSize: '13px', cursor: 'pointer', ...jost,
};

// ── Emotion grid data ──────────────────────────────────────────
const EMOTIONS = [
  { key: 'calm',       emoji: '😌', label: 'Calm' },
  { key: 'focused',    emoji: '🎯', label: 'Focused' },
  { key: 'anxious',    emoji: '😰', label: 'Anxious' },
  { key: 'frustrated', emoji: '😤', label: 'Frustrated' },
  { key: 'excited',    emoji: '🤑', label: 'Excited' },
  { key: 'distracted', emoji: '😴', label: 'Distracted' },
  { key: 'sad',        emoji: '😔', label: 'Sad' },
  { key: 'angry',      emoji: '😠', label: 'Angry' },
  { key: 'numb',       emoji: '🌫️', label: 'Numb' },
  { key: 'rushed',     emoji: '⚡', label: 'Rushed' },
  { key: 'uncertain',  emoji: '😬', label: 'Uncertain' },
  { key: 'confident',  emoji: '💪', label: 'Confident' },
];

// ── Reflection prompts by emotion ──────────────────────────────
const EMOTION_PROMPTS = {
  anxious: [
    "Where do you feel that anxiety in your body right now?",
    "Is this a familiar feeling — does it show up in other areas of your life too?",
    "What would need to be true for you to feel settled enough to trade from your plan today?",
  ],
  rushed: [
    "What's creating the sense of urgency right now?",
    "Is the hurry coming from the market — or from somewhere inside you?",
    "What are you afraid will happen if you slow down?",
  ],
  frustrated: [
    "What happened before you sat down today?",
    "Is this frustration about trading — or did it arrive with you from somewhere else?",
    "What does this part of you most need right now — before you open a position?",
  ],
  confident: [
    "What does this confidence feel like — grounded and clear, or more like excitement?",
    "Is there any part of you that feels uncertain alongside the confidence?",
    "What would you want to remember about this feeling if a difficult trade comes later today?",
  ],
  numb: [
    "Numbness is often protection. What might you be protecting yourself from today?",
    "When did you last feel fully present? What was different then?",
    "Is today a trading day — or a rest day?",
  ],
  angry: [
    "What is the anger pointing at?",
    "Is there a trade you are hoping will prove something today?",
    "What would it mean to step away and come back when this has settled?",
  ],
  excited: [
    "Excitement and urgency can feel similar. Which one is this?",
    "Is there a plan in place that this excitement is supporting — or is the excitement the plan?",
    "What rule are you most tempted to bend today?",
  ],
  sad: [
    "Trading from sadness is trading from a tender place. What is this sadness about?",
    "Does this feeling belong to today — or is it carrying something older?",
    "What does this part of you need that a trade cannot give her?",
  ],
  calm: [
    "What helped you arrive here feeling settled?",
    "Is there anything quietly in the background that hasn't fully surfaced yet?",
    "What would you want to protect about this state as you move through the day?",
  ],
  focused: [
    "What is the focus pointed at — the plan, or a specific outcome you're hoping for?",
    "Is there anything you're deliberately not thinking about right now?",
    "What would it take to stay in this state through the whole session?",
  ],
  distracted: [
    "What's competing for your attention right now?",
    "Is trading today a deliberate choice — or a habit you're falling into?",
    "What would you need to set aside to be fully present here?",
  ],
  uncertain: [
    "What specifically feels uncertain — the setup, your plan, or something else?",
    "Is this uncertainty about this trade, or a broader feeling that's been present lately?",
    "What would it feel like to trade from curiosity rather than from the need to be right?",
  ],
};

// ── The Parts ──────────────────────────────────────────────────
const PARTS = [
  {
    icon: '🛡️', name: 'The Protector', color: V.gold,
    body: "The part that needs the trade to win to prove something. Not about money — about worth. She developed when performance became tied to belonging. She is not trying to sabotage you. She is terrified of what it means if you are wrong.",
  },
  {
    icon: '👁️', name: 'The Avoider', color: V.sky,
    body: "The part that doesn't look at the account when things are hard. That ignores the signal that something isn't working. She learned that not knowing was safer than knowing. She needs to learn that the truth, even when difficult, is always safer than avoidance.",
  },
  {
    icon: '🔥', name: 'The Firefighter', color: V.rose,
    body: "The part that jumps back in immediately after a loss. The revenge trader. She is not irrational — she is desperate to make the pain stop. She needs a better tool than another trade.",
  },
  {
    icon: '🧊', name: 'The Freeze', color: V.lav,
    body: "The part that watches a trade go against her and cannot move. Cannot act. Cannot close. She is not passive — she is overwhelmed. Her nervous system is protecting her the only way it knows how.",
  },
  {
    icon: '🌫️', name: 'The Doubter', color: V.muted,
    body: "The part that whispers 'I don't really know what I'm doing' on the days when everything is working. Imposter syndrome is not a personality flaw. It is a part that learned to expect the good things to be taken away.",
  },
  {
    icon: '🌿', name: 'The Griever', color: V.sage,
    body: "The part that feels loss disproportionately — not just the money, but what it means. She may carry older losses than this trade. She deserves to be heard, not suppressed.",
  },
];

// ── Books & resources ──────────────────────────────────────────
const BOOK_SECTIONS = [
  {
    key: 'lynn',
    title: "Lynn's Personal Bookshelf",
    intro: "These are books that have genuinely shaped how I see myself, money, and what's possible. Some are about trading. Most aren't. All of them changed something in me.",
    items: [
      { title: "Who Moved My Cheese", author: "Spencer Johnson", note: "Deceptively simple. Profoundly true about how we resist change even when staying is hurting us." },
      { title: "The Richest Man in Babylon", author: "George S. Clason", note: "The principles here are timeless. I wish I had read this at 20." },
      { title: "Her First $100K", author: "Tori Dunlap", note: "Tori is doing what we're doing — demystifying money for women. Read everything she writes." },
      { title: "The writings of Charlie Munger / Berkshire Hathaway letters", author: "Charlie Munger", note: "Mental models. Clear thinking. The long game. Munger understood that investing is really about understanding human psychology — including your own." },
    ],
  },
  {
    key: 'inner',
    title: "For The Inner Work",
    items: [
      { title: "No Bad Parts", author: "Richard Schwartz", note: "The book that introduced me to the framework behind this section. Start here." },
      { title: "The Body Keeps the Score", author: "Bessel van der Kolk", note: "If you have ever felt physical symptoms from emotional stress — and traders do — this will explain everything." },
      { title: "The Big Leap", author: "Gay Hendricks", note: "About upper limiting — sabotaging yourself right at the moment things start working. You may recognize yourself here." },
      { title: "Women Who Run With the Wolves", author: "Clarissa Pinkola Estés", note: "About reclaiming the instinctual, knowing part of yourself that gets quieted by life. Essential." },
      { title: "Untamed", author: "Glennon Doyle", note: "Permission to stop performing and start trusting yourself." },
      { title: "Self-Therapy", author: "Jay Earley", note: "A practical workbook for doing inner work on your own." },
    ],
  },
  {
    key: 'trading',
    title: "For Trading Psychology",
    items: [
      { title: "Trading in the Zone", author: "Mark Douglas", note: "The psychology of trading. The market is a mirror. Douglas understood this." },
      { title: "The Psychology of Money", author: "Morgan Housel", note: "Behavior matters more than knowledge. This book proves it beautifully." },
      { title: "Thinking, Fast and Slow", author: "Daniel Kahneman", note: "Why we make the decisions we make under uncertainty. Every trader should read this." },
      { title: "Reminiscences of a Stock Operator", author: "Edwin Lefèvre", note: "The emotional truth of trading told as story. A century old and still completely true." },
    ],
  },
  {
    key: 'podcasts',
    title: "Podcasts & Resources",
    items: [
      { title: "tastylive", author: "tastylive.com", note: "The best free options education available. Watch their research." },
      { title: "SMB Capital", author: "YouTube", note: "Professional trading education. High quality." },
      { title: "We Can Do Hard Things", author: "Glennon Doyle", note: "Healing and self-discovery. Not about trading. Essential anyway." },
      { title: "Chat With Traders", author: "Podcast", note: "Real trader interviews, heavy on psychology." },
      { title: "Her First $100K Podcast", author: "Tori Dunlap", note: "Money and self-worth for women." },
    ],
  },
];

const TABS = [
  { key: 'intro',      label: 'Introduction' },
  { key: 'parts',      label: 'Your Parts' },
  { key: 'pretrade',   label: 'Before You Trade' },
  { key: 'posttrade',  label: 'After The Trade' },
  { key: 'mirror',     label: 'The Belief Mirror' },
  { key: 'resources',  label: 'Resources' },
];

// ── Shared textarea style ──────────────────────────────────────
const makeTa = (minH = 90) => ({
  width: '100%', padding: '12px 14px',
  background: V.bg3,
  borderTop: `0.5px solid ${V.border}`,
  borderRight: `0.5px solid ${V.border}`,
  borderBottom: `0.5px solid ${V.border}`,
  borderLeft: `0.5px solid ${V.border}`,
  borderRadius: '8px', color: V.text,
  fontSize: '13px', outline: 'none',
  resize: 'vertical', minHeight: `${minH}px`,
  ...jost, lineHeight: 1.6, marginTop: '10px',
});

// ── Emotion toggle button ──────────────────────────────────────
function EmotionBtn({ emotion, selected, onToggle }) {
  return (
    <button
      onClick={() => onToggle(emotion.key)}
      style={{
        padding: '12px 6px',
        background: selected ? 'rgba(201,168,76,0.12)' : V.bg2,
        borderTop:    `0.5px solid ${selected ? V.gold : V.border}`,
        borderRight:  `0.5px solid ${selected ? V.gold : V.border}`,
        borderBottom: `0.5px solid ${selected ? V.gold : V.border}`,
        borderLeft:   `0.5px solid ${selected ? V.gold : V.border}`,
        borderRadius: '10px', cursor: 'pointer',
        color: selected ? V.goldL : V.muted,
        fontSize: '13px', ...jost,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
        transition: 'all 0.15s',
      }}
    >
      <span style={{ fontSize: '20px' }}>{emotion.emoji}</span>
      <span>{emotion.label}</span>
    </button>
  );
}

// ── SECTION 1 — Introduction ───────────────────────────────────
function IntroSection({ onNext }) {
  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '44px 24px 64px' }}>

      {/* Quotes */}
      <div style={{ marginBottom: '44px' }}>
        {[
          { q: '"The most common form of despair is not being who you are."', attr: '— Søren Kierkegaard' },
          { q: '"Knowing yourself is the beginning of all wisdom."',           attr: '— Aristotle' },
        ].map(({ q, attr }, i) => (
          <div key={i} style={{
            borderLeft: `3px solid ${V.gold}`,
            paddingLeft: '22px', marginBottom: '28px',
          }}>
            <p style={{ ...serif, fontSize: '22px', fontStyle: 'italic', color: V.gold, lineHeight: 1.55, marginBottom: '8px' }}>{q}</p>
            <p style={{ ...serif, fontSize: '14px', color: V.muted, fontStyle: 'italic' }}>{attr}</p>
          </div>
        ))}
      </div>

      {/* Lynn's letter */}
      <div style={{ fontSize: '15px', lineHeight: 1.9, color: 'rgba(240,237,230,0.85)', ...jost }}>
        <p style={{ marginBottom: '22px' }}>I want to tell you something I didn't expect when I started trading.</p>
        <p style={{ marginBottom: '22px' }}>I thought I was learning about markets. I was actually learning about myself.</p>
        <p style={{ marginBottom: '22px' }}>First — you belong here. Research consistently shows that women are statistically better investors than men. We are more patient, more disciplined, less likely to overtrade, and more willing to follow a plan. The traits that make a great option seller are traits you already have. The industry has just been slow to notice.</p>
        <p style={{ marginBottom: '22px' }}>Many of us arrive at trading carrying patterns we developed long before we ever heard the word 'options.' Ways of responding to uncertainty, to loss, to unexpected success, shaped by life long before the market had a chance to test them. Trading reveals all of it. The market has no interest in our coping strategies. It will find them — and hold them up to the light.</p>
        <p style={{ marginBottom: '22px' }}>Every pattern in our trading tends to be a pattern somewhere in our life. Every belief we carry about money tends to reflect a belief we carry about our own worth. This is not a flaw. It is an invitation.</p>
        <p style={{ marginBottom: '22px' }}>The most powerful breakthroughs in trading rarely come from learning a new strategy. They come from understanding ourselves more honestly. From getting curious about the parts of us that are quietly running the show. From asking not just 'what did the market do' — but 'what did I do, and why, and where else in my life does that show up?'</p>
        <p style={{ marginBottom: '22px' }}>The questions here don't have right answers. They are simply an invitation to hear yourself more clearly.</p>
        <p style={{ marginBottom: '32px' }}>The market has been one of the most honest mirrors I have ever looked into. I hope it becomes that for you too — not just a place to grow your account, but a place to grow into yourself.</p>
        <p style={{ ...serif, fontSize: '20px', fontStyle: 'italic', color: V.goldL }}>— Lynn</p>
      </div>

      <div style={{ textAlign: 'center', marginTop: '48px' }}>
        <button style={{ ...btnGold, padding: '14px 36px', fontSize: '15px' }} onClick={onNext}>
          Begin The Inner Work →
        </button>
      </div>
    </div>
  );
}

// ── SECTION 2 — Understanding Your Parts ──────────────────────
function PartsSection({ onNext }) {
  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '44px 24px 64px' }}>
      <h2 style={{ ...serif, fontSize: '30px', fontWeight: 500, color: V.text, marginBottom: '8px' }}>Understanding Your Parts</h2>
      <p style={{ ...serif, fontSize: '17px', fontStyle: 'italic', color: V.muted, marginBottom: '32px' }}>A framework for getting curious instead of critical</p>

      <div style={{ fontSize: '14px', lineHeight: 1.85, color: 'rgba(240,237,230,0.82)', ...jost, marginBottom: '36px' }}>
        <p style={{ marginBottom: '20px' }}>There is a framework that many people find transformative in their inner work. It begins with a simple but radical idea: there are no bad parts of us.</p>
        <p style={{ marginBottom: '20px' }}>Every pattern, every habit, every response that seems to work against us — developed for a reason. It was trying to protect us from something. The part that overrides a stop loss isn't stupidity. It may be a part that learned long ago that giving up means not being enough. The part that won't pull the trigger on a perfect setup isn't weakness. It may be a part that decided not trying was safer than failing.</p>
        <p style={{ marginBottom: '20px' }}>When we can meet these parts of ourselves with curiosity instead of judgment — when we can ask them what they are afraid of, what they are protecting us from — something begins to shift. We stop fighting ourselves. We start understanding ourselves.</p>
        <p>In trading, these parts often look like this:</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '16px', marginBottom: '40px' }}>
        {PARTS.map(({ icon, name, color, body }) => (
          <div key={name} style={{
            background: V.bg2, borderRadius: '12px', padding: '22px 22px',
            borderTop:    `0.5px solid ${V.border}`,
            borderRight:  `0.5px solid ${V.border}`,
            borderBottom: `0.5px solid ${V.border}`,
            borderLeft:   `3px solid ${color}`,
          }}>
            <div style={{ fontSize: '26px', marginBottom: '10px' }}>{icon}</div>
            <div style={{ ...serif, fontSize: '18px', fontWeight: 500, color, marginBottom: '10px' }}>{name}</div>
            <p style={{ fontSize: '13px', lineHeight: 1.78, color: V.muted, ...jost }}>{body}</p>
          </div>
        ))}
      </div>

      <p style={{
        ...serif, fontSize: '17px', fontStyle: 'italic', color: V.muted,
        lineHeight: 1.75, textAlign: 'center', marginBottom: '40px',
        maxWidth: '560px', margin: '0 auto 40px',
      }}>
        You may recognize yourself in one of these. You may see yourself in all of them at different times. None of them need to be fixed. They need to be understood. That is what the check-ins below are for.
      </p>

      <div style={{ textAlign: 'center' }}>
        <button style={{ ...btnGold, padding: '14px 36px', fontSize: '15px' }} onClick={onNext}>
          Continue to Check-Ins →
        </button>
      </div>
    </div>
  );
}

// ── SECTION 3 — Pre-Trade Check-In ────────────────────────────
function PreTradeSection({ state, onUpdate }) {
  const [selectedEmotions, setSelectedEmotions] = useState([]);
  const [answers, setAnswers]       = useState({});
  const [bodyTension, setBodyTension] = useState([]);
  const [readiness, setReadiness]   = useState(7);
  const [saved, setSaved]           = useState(false);

  const toggleEmotion = (key) => setSelectedEmotions(prev =>
    prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
  );
  const toggleBody = (part) => setBodyTension(prev =>
    prev.includes(part) ? prev.filter(p => p !== part) : [...prev, part]
  );

  const activePrompts = selectedEmotions.flatMap(key =>
    (EMOTION_PROMPTS[key] || []).map(p => ({ key, prompt: p }))
  );

  const saveCheckin = () => {
    const checkin = {
      id: Date.now(),
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      type: 'pre',
      emotions: selectedEmotions,
      answers,
      bodyTension,
      readiness,
    };
    onUpdate({ innerWorkCheckins: [...(state.innerWorkCheckins || []), checkin] });
    setSaved(true);
  };

  const reset = () => {
    setSelectedEmotions([]); setAnswers({});
    setBodyTension([]); setReadiness(7); setSaved(false);
  };

  const ta = makeTa(80);

  if (saved) return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
      <div style={{ ...serif, fontSize: '36px', color: V.gold, marginBottom: '20px' }}>✦</div>
      <h3 style={{ ...serif, fontSize: '26px', color: V.goldL, marginBottom: '14px' }}>Check-in saved.</h3>
      <p style={{ fontSize: '14px', color: V.muted, lineHeight: 1.8, marginBottom: '36px', ...jost }}>
        You've taken a moment to arrive honestly. That is not a small thing. Trade from here.
      </p>
      <button style={{ ...btnGold }} onClick={reset}>Start Another Check-In</button>
    </div>
  );

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '44px 24px 64px' }}>
      <h2 style={{ ...serif, fontSize: '30px', fontWeight: 500, color: V.text, marginBottom: '8px' }}>Before You Trade</h2>
      <p style={{ ...serif, fontSize: '17px', fontStyle: 'italic', color: V.muted, marginBottom: '16px' }}>A moment to arrive</p>
      <p style={{ fontSize: '14px', color: V.muted, lineHeight: 1.8, marginBottom: '40px', ...jost }}>
        Before you open a position, take 60 seconds here. Not to talk yourself into or out of a trade — just to notice where you are arriving from. Honest answers only. This is for you.
      </p>

      {/* Step 1 — Emotions */}
      <div style={{ marginBottom: '38px' }}>
        <div style={{ ...eyebrow, marginBottom: '16px' }}>Step 1 — How are you arriving at the market today?</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
          {EMOTIONS.map(e => (
            <EmotionBtn key={e.key} emotion={e} selected={selectedEmotions.includes(e.key)} onToggle={toggleEmotion} />
          ))}
        </div>
      </div>

      {/* Step 2 — Reflection prompts */}
      {activePrompts.length > 0 && (
        <div style={{ marginBottom: '38px' }}>
          <div style={{ ...eyebrow, marginBottom: '20px' }}>Step 2 — A few questions to sit with</div>
          {activePrompts.map(({ prompt }, i) => (
            <div key={i} style={{ marginBottom: '22px' }}>
              <p style={{ fontSize: '14px', color: V.text, lineHeight: 1.75, ...jost }}>{prompt}</p>
              <textarea
                style={ta}
                placeholder="Your thoughts (optional — this is for you, not for anyone else)"
                value={answers[prompt] || ''}
                onChange={e => setAnswers(a => ({ ...a, [prompt]: e.target.value }))}
              />
            </div>
          ))}
        </div>
      )}

      {/* Step 3 — Body check */}
      <div style={{ marginBottom: '38px' }}>
        <div style={{ ...eyebrow, marginBottom: '10px' }}>Step 3 — Where are you holding tension right now?</div>
        <p style={{ fontSize: '13px', color: V.muted, marginBottom: '16px', ...jost }}>Check all that apply</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {['Chest', 'Jaw', 'Shoulders', 'Stomach', 'Throat', 'Hands', 'Nowhere — I feel settled'].map(part => {
            const on = bodyTension.includes(part);
            return (
              <button
                key={part}
                onClick={() => toggleBody(part)}
                style={{
                  padding: '9px 18px',
                  background: on ? 'rgba(201,168,76,0.12)' : V.bg2,
                  borderTop:    `0.5px solid ${on ? V.gold : V.border}`,
                  borderRight:  `0.5px solid ${on ? V.gold : V.border}`,
                  borderBottom: `0.5px solid ${on ? V.gold : V.border}`,
                  borderLeft:   `0.5px solid ${on ? V.gold : V.border}`,
                  borderRadius: '20px', cursor: 'pointer',
                  color: on ? V.goldL : V.muted,
                  fontSize: '13px', ...jost, transition: 'all 0.15s',
                }}
              >
                {part}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 4 — Readiness */}
      <div style={{ marginBottom: '38px' }}>
        <div style={{ ...eyebrow, marginBottom: '14px' }}>Step 4 — Plan Alignment</div>
        <p style={{ fontSize: '14px', color: V.text, lineHeight: 1.75, marginBottom: '18px', ...jost }}>
          On a scale of 1–10, how aligned do you feel with your trading plan right now?
        </p>
        <input
          type="range" min="1" max="10" value={readiness}
          onChange={e => setReadiness(parseInt(e.target.value))}
          style={{ width: '100%', accentColor: V.gold, cursor: 'pointer', marginBottom: '10px' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: V.dim, marginBottom: '16px', ...jost }}>
          <span>1 — A part of me wants to go off-script</span>
          <span style={{ ...serif, fontSize: '24px', color: V.gold, fontWeight: 500 }}>{readiness}</span>
          <span>10 — I am clear and ready</span>
        </div>
        {readiness <= 4 && (
          <div style={{ padding: '16px 20px', background: 'rgba(201,168,76,0.07)', borderTop: `0.5px solid ${V.gold}`, borderRight: `0.5px solid ${V.gold}`, borderBottom: `0.5px solid ${V.gold}`, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', fontSize: '13px', color: V.goldL, lineHeight: 1.75, ...jost }}>
            A score this low is information, not failure. Something in you is telling you something. You don't have to trade today. The market will be here tomorrow.
          </div>
        )}
        {readiness >= 5 && readiness <= 7 && (
          <div style={{ padding: '16px 20px', background: 'rgba(122,158,196,0.07)', borderTop: `0.5px solid ${V.sky}`, borderRight: `0.5px solid ${V.sky}`, borderBottom: `0.5px solid ${V.sky}`, borderLeft: `3px solid ${V.sky}`, borderRadius: '10px', fontSize: '13px', color: V.sky, lineHeight: 1.75, ...jost }}>
            Notice what's creating the gap. Name it if you can. Then decide consciously — not automatically.
          </div>
        )}
        {readiness >= 8 && (
          <div style={{ padding: '16px 20px', background: 'rgba(122,171,138,0.07)', borderTop: `0.5px solid ${V.sage}`, borderRight: `0.5px solid ${V.sage}`, borderBottom: `0.5px solid ${V.sage}`, borderLeft: `3px solid ${V.sage}`, borderRadius: '10px', fontSize: '13px', color: V.sage, lineHeight: 1.75, ...jost }}>
            This is the state to trade from. Notice what created it. It's worth remembering.
          </div>
        )}
      </div>

      {/* Save */}
      <div style={{ borderTop: `0.5px solid ${V.border}`, paddingTop: '30px' }}>
        <button
          style={{ ...btnGold, padding: '14px 36px', fontSize: '14px', opacity: selectedEmotions.length > 0 ? 1 : 0.4, cursor: selectedEmotions.length > 0 ? 'pointer' : 'default' }}
          onClick={selectedEmotions.length > 0 ? saveCheckin : undefined}
        >
          Save Check-In & Begin Trading →
        </button>
        {selectedEmotions.length === 0 && (
          <p style={{ fontSize: '12px', color: V.dim, marginTop: '12px', ...jost }}>
            Select at least one emotion to continue.
          </p>
        )}
      </div>
    </div>
  );
}

// ── SECTION 4 — Post-Trade Reflection ─────────────────────────
function PostTradeSection({ state, onUpdate, initialTrade }) {
  const [q1, setQ1]               = useState('');
  const [q2override, setQ2]       = useState(null);
  const [q2detail, setQ2D]        = useState('');
  const [q3followed, setQ3]       = useState(null);
  const [q3detail, setQ3D]        = useState('');
  const [postEmotions, setPostEm] = useState([]);
  const [q5life, setQ5]           = useState('');
  const [q6market, setQ6M]        = useState('');
  const [q6self, setQ6S]          = useState('');
  const [q7message, setQ7]        = useState('');
  const [saved, setSaved]         = useState(false);

  const toggleEmotion = (key) => setPostEm(prev =>
    prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
  );

  const saveReflection = () => {
    const reflection = {
      id: Date.now(),
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      type: 'post',
      q1, q2override, q2detail: q2detail,
      q3followed, q3detail: q3detail,
      postEmotions, q5life,
      q6market, q6self, q7message,
    };
    onUpdate({ innerWorkReflections: [...(state.innerWorkReflections || []), reflection] });
    setSaved(true);
  };

  const reset = () => {
    setQ1(''); setQ2(null); setQ2D('');
    setQ3(null); setQ3D(''); setPostEm([]);
    setQ5(''); setQ6M(''); setQ6S(''); setQ7(''); setSaved(false);
  };

  const ta    = makeTa(100);
  const taSm  = makeTa(72);

  const choiceBtn = (active, color = V.gold) => ({
    padding: '10px 22px',
    background: active ? 'rgba(201,168,76,0.12)' : V.bg2,
    borderTop:    `0.5px solid ${active ? color : V.border}`,
    borderRight:  `0.5px solid ${active ? color : V.border}`,
    borderBottom: `0.5px solid ${active ? color : V.border}`,
    borderLeft:   `0.5px solid ${active ? color : V.border}`,
    borderRadius: '8px', cursor: 'pointer',
    color: active ? V.goldL : V.muted,
    fontSize: '13px', ...jost, transition: 'all 0.15s',
  });

  if (saved) return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
      <div style={{ ...serif, fontSize: '36px', color: V.gold, marginBottom: '20px' }}>✦</div>
      <h3 style={{ ...serif, fontSize: '26px', color: V.goldL, marginBottom: '14px' }}>Reflection saved.</h3>
      <p style={{ fontSize: '14px', color: V.muted, lineHeight: 1.8, marginBottom: '36px', ...jost }}>
        You looked honestly at what happened. That is where the real growth lives.
      </p>
      <button style={{ ...btnGold }} onClick={reset}>Reflect on Another Trade</button>
    </div>
  );

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '44px 24px 64px' }}>
      <h2 style={{ ...serif, fontSize: '30px', fontWeight: 500, color: V.text, marginBottom: '8px' }}>After The Trade</h2>
      <p style={{ ...serif, fontSize: '17px', fontStyle: 'italic', color: V.muted, marginBottom: '16px' }}>What just happened — inside and out</p>
      <p style={{ fontSize: '14px', color: V.muted, lineHeight: 1.8, marginBottom: initialTrade ? '24px' : '40px', ...jost }}>
        This is where the real learning happens. Not in the profit or loss — but in what you notice about yourself when you look honestly at what just occurred.
      </p>

      {/* Pre-populated trade context */}
      {initialTrade && (
        <div style={{
          marginBottom: '36px', padding: '16px 20px',
          background: 'rgba(201,168,76,0.06)',
          borderTop:    `0.5px solid rgba(201,168,76,0.2)`,
          borderRight:  `0.5px solid rgba(201,168,76,0.2)`,
          borderBottom: `0.5px solid rgba(201,168,76,0.2)`,
          borderLeft:   `3px solid ${V.gold}`,
          borderRadius: '10px',
          display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: V.gold, ...jost, flexShrink: 0 }}>Reflecting on:</div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px', color: V.goldL }}>{initialTrade.ticker}</span>
            <span style={{ fontSize: '13px', color: V.muted, ...jost }}>{initialTrade.strategy}</span>
            {initialTrade.strike && <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '13px', color: V.text }}>${initialTrade.strike} strike</span>}
            {initialTrade.pnl
              ? <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '13px', color: parseFloat(initialTrade.pnl) >= 0 ? V.sage : V.rose }}>{parseFloat(initialTrade.pnl) >= 0 ? '+' : ''}${initialTrade.pnl}</span>
              : <span style={{ fontSize: '13px', color: V.sky, ...jost }}>{initialTrade.status}</span>
            }
          </div>
        </div>
      )}

      {/* Q1 */}
      <div style={{ marginBottom: '34px' }}>
        <p style={{ fontSize: '15px', color: V.text, lineHeight: 1.75, ...jost }}>
          What happened — not just in the trade, but inside you while it was happening?
        </p>
        <textarea style={ta} placeholder="This is for you — write as openly as you can" value={q1} onChange={e => setQ1(e.target.value)} />
      </div>

      {/* Q2 */}
      <div style={{ marginBottom: '34px' }}>
        <p style={{ fontSize: '15px', color: V.text, lineHeight: 1.75, marginBottom: '14px', ...jost }}>
          Was there a moment where you noticed a part of you wanting to override your plan?
        </p>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {['Yes', 'No', "I'm not sure"].map(v => (
            <button key={v} style={choiceBtn(q2override === v)} onClick={() => setQ2(v)}>{v}</button>
          ))}
        </div>
        {q2override === 'Yes' && (
          <>
            <p style={{ fontSize: '13px', color: V.muted, ...jost }}>What did that feel like? What was the thought or impulse that showed up?</p>
            <textarea style={taSm} placeholder="This is for you" value={q2detail} onChange={e => setQ2D(e.target.value)} />
          </>
        )}
      </div>

      {/* Q3 */}
      <div style={{ marginBottom: '34px' }}>
        <p style={{ fontSize: '15px', color: V.text, lineHeight: 1.75, marginBottom: '14px', ...jost }}>
          Did you follow your plan?
        </p>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {['Fully', 'Mostly', 'Partially', 'No'].map(v => (
            <button key={v} style={choiceBtn(q3followed === v)} onClick={() => setQ3(v)}>{v}</button>
          ))}
        </div>
        {q3followed && q3followed !== 'Fully' && (
          <>
            <p style={{ fontSize: '13px', color: V.muted, ...jost }}>What pulled you away from it? Can you describe the feeling or thought that did?</p>
            <textarea style={taSm} placeholder="This is for you" value={q3detail} onChange={e => setQ3D(e.target.value)} />
          </>
        )}
      </div>

      {/* Q4 */}
      <div style={{ marginBottom: '34px' }}>
        <p style={{ fontSize: '15px', color: V.text, lineHeight: 1.75, marginBottom: '16px', ...jost }}>
          How are you feeling now that it's over?
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
          {EMOTIONS.map(e => (
            <EmotionBtn key={e.key} emotion={e} selected={postEmotions.includes(e.key)} onToggle={toggleEmotion} />
          ))}
        </div>
      </div>

      {/* Q5 — The Life Question */}
      <div style={{
        marginBottom: '34px', padding: '24px 26px',
        background: 'rgba(201,168,76,0.04)',
        borderTop:    `0.5px solid rgba(201,168,76,0.25)`,
        borderRight:  `0.5px solid rgba(201,168,76,0.25)`,
        borderBottom: `0.5px solid rgba(201,168,76,0.25)`,
        borderLeft:   `3px solid rgba(201,168,76,0.4)`,
        borderRadius: '12px',
      }}>
        <p style={{ fontSize: '15px', color: V.text, lineHeight: 1.8, ...jost, marginBottom: '14px' }}>
          Without judgment — just curiosity — does the way you responded in this trade feel familiar? Does this pattern show up anywhere else in your life? In how you handle uncertainty, or discomfort, or unexpected outcomes? You don't have to answer now. Just let the question sit with you.
        </p>
        <textarea
          style={{ ...makeTa(90) }}
          placeholder="If something comes up, write it here. This is only for you."
          value={q5life} onChange={e => setQ5(e.target.value)}
        />
      </div>

      {/* Q6 */}
      <div style={{ marginBottom: '34px' }}>
        <p style={{ fontSize: '15px', color: V.text, lineHeight: 1.75, marginBottom: '18px', ...jost }}>
          What did this trade teach you — about the market, and about yourself?
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <div style={{ ...eyebrow, marginBottom: '8px' }}>About the market:</div>
            <textarea style={taSm} placeholder="This is for you" value={q6market} onChange={e => setQ6M(e.target.value)} />
          </div>
          <div>
            <div style={{ ...eyebrow, marginBottom: '8px' }}>About myself:</div>
            <textarea style={taSm} placeholder="This is for you" value={q6self} onChange={e => setQ6S(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Q7 */}
      <div style={{ marginBottom: '38px' }}>
        <p style={{ fontSize: '15px', color: V.text, lineHeight: 1.75, ...jost }}>
          What do you want to say to yourself about how you handled this?
        </p>
        <textarea style={ta} placeholder="This is for you" value={q7message} onChange={e => setQ7(e.target.value)} />
        <p style={{ fontSize: '12px', color: V.dim, fontStyle: 'italic', marginTop: '12px', ...jost }}>
          Most of us are harder on ourselves than we would ever be on someone we love. You are allowed to be fair to yourself here.
        </p>
      </div>

      {/* Save */}
      <div style={{ borderTop: `0.5px solid ${V.border}`, paddingTop: '30px' }}>
        <button style={{ ...btnGold, padding: '14px 36px', fontSize: '14px' }} onClick={saveReflection}>
          Save Reflection →
        </button>
      </div>
    </div>
  );
}

// ── SECTION 5 — The Belief Mirror ─────────────────────────────
function BeliefMirrorSection({ state }) {
  const checkins    = state.innerWorkCheckins    || [];
  const reflections = state.innerWorkReflections || [];
  const [patternAns, setPatternAns] = useState('');
  const [planAns,    setPlanAns]    = useState('');
  const [lifeAns,    setLifeAns]    = useState('');

  const ta = makeTa(80);

  if (checkins.length < 10) return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
      <div style={{ ...serif, fontSize: '36px', color: V.gold, marginBottom: '20px' }}>✦</div>
      <h2 style={{ ...serif, fontSize: '28px', color: V.goldL, marginBottom: '16px' }}>The Belief Mirror</h2>
      <p style={{ fontSize: '14px', color: V.muted, lineHeight: 1.85, ...jost }}>
        The Belief Mirror will begin to reflect your patterns back to you after you have completed 10 check-ins. Every entry is building a picture.
        <br /><br />
        <span style={{ color: V.gold, fontSize: '16px' }}>{checkins.length}</span>
        <span style={{ color: V.muted }}> of </span>
        <span style={{ color: V.gold, fontSize: '16px' }}>10</span>
        <span style={{ color: V.muted }}> check-ins completed.</span>
        <br /><br />
        Come back to this as you go.
      </p>
    </div>
  );

  // Pattern analysis — handles both full check-ins (type:'pre') and inline trade check-ins (type:'pre_trade')
  const preCheckins = checkins.filter(c => c.type === 'pre' || c.type === 'pre_trade');
  const emotionFreq = {};
  preCheckins.forEach(c => {
    // full check-in: c.emotions is array; inline: c.emotion is string
    if (Array.isArray(c.emotions)) c.emotions.forEach(e => { emotionFreq[e] = (emotionFreq[e] || 0) + 1; });
    else if (c.emotion) { emotionFreq[c.emotion] = (emotionFreq[c.emotion] || 0) + 1; }
  });
  const topPreEmotions = Object.entries(emotionFreq)
    .sort((a, b) => b[1] - a[1]).slice(0, 3)
    .map(([k]) => EMOTIONS.find(e => e.key === k)).filter(Boolean);

  const postCheckins   = checkins.filter(c => c.type === 'post_trade');
  const postReflections = reflections.filter(r => r.type === 'post');
  const postEmFreq = {};
  postReflections.forEach(r => (r.postEmotions || []).forEach(e => { postEmFreq[e] = (postEmFreq[e] || 0) + 1; }));
  postCheckins.forEach(c => { if (c.emotion) { postEmFreq[c.emotion] = (postEmFreq[c.emotion] || 0) + 1; } });
  const topPostEmotions = Object.entries(postEmFreq)
    .sort((a, b) => b[1] - a[1]).slice(0, 3)
    .map(([k]) => EMOTIONS.find(e => e.key === k)).filter(Boolean);

  const fullPreCheckins = checkins.filter(c => c.type === 'pre');
  const highDays    = fullPreCheckins.filter(c => c.readiness >= 7).length;
  const lowDays     = fullPreCheckins.filter(c => c.readiness <= 4);
  const lowEmLabels = [...new Set(lowDays.flatMap(c => c.emotions || []))]
    .map(k => EMOTIONS.find(e => e.key === k)?.label).filter(Boolean);

  const lifeReflections = postReflections.filter(r => r.q5life && r.q5life.trim());

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto', padding: '44px 24px 64px' }}>
      <style>{`@keyframes shimmer{0%{background-position:200% center}100%{background-position:-200% center}}`}</style>

      <h2 style={{
        ...serif, fontSize: '32px', fontWeight: 500, marginBottom: '8px',
        background: `linear-gradient(90deg,${V.gold},${V.goldL},#fff8e0,${V.goldL},${V.gold})`,
        backgroundSize: '200% auto', WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent', animation: 'shimmer 4s linear infinite',
        display: 'inline-block',
      }}>The Belief Mirror</h2>
      <p style={{ ...serif, fontSize: '17px', fontStyle: 'italic', color: V.muted, marginBottom: '36px' }}>
        What the data is beginning to show you
      </p>

      {/* Emotional Patterns */}
      <div style={{ ...card, marginBottom: '20px' }}>
        <div style={{ ...eyebrow, marginBottom: '16px' }}>Emotional Patterns</div>
        {topPreEmotions.length > 0 && (
          <p style={{ fontSize: '13px', color: V.muted, marginBottom: '10px', lineHeight: 1.75, ...jost }}>
            Your most frequent pre-trade emotions have been:{' '}
            <span style={{ color: V.text }}>{topPreEmotions.map(e => `${e.emoji} ${e.label}`).join(', ')}</span>
          </p>
        )}
        {topPostEmotions.length > 0 && (
          <p style={{ fontSize: '13px', color: V.muted, marginBottom: '18px', lineHeight: 1.75, ...jost }}>
            Your most frequent post-trade emotions have been:{' '}
            <span style={{ color: V.text }}>{topPostEmotions.map(e => `${e.emoji} ${e.label}`).join(', ')}</span>
          </p>
        )}
        <p style={{ fontSize: '14px', color: V.text, ...jost, marginBottom: '6px' }}>What do you notice about that?</p>
        <textarea style={ta} placeholder="This is for you" value={patternAns} onChange={e => setPatternAns(e.target.value)} />
      </div>

      {/* Plan Alignment */}
      <div style={{ ...card, marginBottom: '20px' }}>
        <div style={{ ...eyebrow, marginBottom: '16px' }}>Plan Alignment</div>
        <p style={{ fontSize: '13px', color: V.muted, marginBottom: '10px', lineHeight: 1.75, ...jost }}>
          You have rated your plan alignment at 7 or above on{' '}
          <span style={{ color: V.goldL }}>{highDays}</span> out of{' '}
          <span style={{ color: V.goldL }}>{fullPreCheckins.length}</span> trading days.
        </p>
        {lowEmLabels.length > 0 && (
          <p style={{ fontSize: '13px', color: V.muted, marginBottom: '18px', lineHeight: 1.75, ...jost }}>
            On the days you rated yourself lowest, these emotions were most present:{' '}
            <span style={{ color: V.text }}>{lowEmLabels.join(', ')}</span>
          </p>
        )}
        <p style={{ fontSize: '14px', color: V.text, ...jost, marginBottom: '6px' }}>What does this pattern tell you — in your own words?</p>
        <textarea style={ta} placeholder="This is for you" value={planAns} onChange={e => setPlanAns(e.target.value)} />
      </div>

      {/* The Life Question */}
      {lifeReflections.length > 0 && (
        <div style={{ ...card, background: 'rgba(201,168,76,0.04)', borderTop: `0.5px solid rgba(201,168,76,0.2)`, borderRight: `0.5px solid rgba(201,168,76,0.2)`, borderBottom: `0.5px solid rgba(201,168,76,0.2)`, borderLeft: `3px solid rgba(201,168,76,0.4)` }}>
          <div style={{ ...eyebrow, marginBottom: '16px' }}>The Life Question</div>
          <p style={{ fontSize: '13px', color: V.muted, marginBottom: '20px', lineHeight: 1.75, ...jost }}>
            You have been noticing something. Here is what you have written, in your own words:
          </p>
          {lifeReflections.map(r => (
            <div key={r.id} style={{ borderLeft: `2px solid rgba(201,168,76,0.3)`, paddingLeft: '18px', marginBottom: '18px' }}>
              <p style={{ fontSize: '11px', color: V.dim, marginBottom: '6px', ...jost }}>{r.date}</p>
              <p style={{ fontSize: '14px', color: 'rgba(240,237,230,0.82)', lineHeight: 1.75, fontStyle: 'italic', ...jost }}>{r.q5life}</p>
            </div>
          ))}
          <p style={{ fontSize: '14px', color: V.text, ...jost, marginBottom: '6px', marginTop: '22px' }}>
            Read these as if a friend wrote them to you. What would you want to say to her?
          </p>
          <textarea style={ta} placeholder="This is for you" value={lifeAns} onChange={e => setLifeAns(e.target.value)} />
        </div>
      )}
    </div>
  );
}

// ── SECTION 6 — Resources ──────────────────────────────────────
function ResourcesSection() {
  const [open, setOpen] = useState({ lynn: true, inner: false, trading: false, podcasts: false });
  const toggle = (key) => setOpen(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '44px 24px 64px' }}>
      <h2 style={{ ...serif, fontSize: '30px', fontWeight: 500, color: V.text, marginBottom: '8px' }}>The Reading List</h2>
      <p style={{ ...serif, fontSize: '17px', fontStyle: 'italic', color: V.muted, marginBottom: '36px' }}>Books and resources that go deeper</p>

      {/* Erin Doumert — Practitioner card */}
      <div style={{ ...card, marginBottom: '28px' }}>
        <div style={{ display: 'inline-block', padding: '3px 10px', background: 'rgba(122,171,138,0.12)', border: '0.5px solid rgba(122,171,138,0.35)', borderRadius: '20px', marginBottom: '18px' }}>
          <span style={{ ...eyebrow, color: V.sage }}>Nervous System &amp; Somatic Work</span>
        </div>

        <div style={{ ...serif, fontSize: '20px', color: V.gold, marginBottom: '4px' }}>Erin Doumert</div>
        <div style={{ ...jost, fontSize: '13px', fontStyle: 'italic', color: V.muted, marginBottom: '18px' }}>Somatic Experiencing Practitioner &amp; Trader</div>

        <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.8, marginBottom: '20px' }}>
          You know the strategy. So why does your trading keep falling apart?
          {'\n\n'}
          This isn't a strategy problem. It's your nervous system — and it's been running the show.
        </p>

        <div style={{ background: V.bg3, borderLeft: `3px solid ${V.gold}`, borderRadius: '0 8px 8px 0', padding: '16px', marginBottom: '20px' }}>
          <p style={{ ...jost, fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: 0 }}>
            Erin works with funded traders and serious retail traders on the patterns living in the body that override everything the mind knows.
            {'\n\n'}
            This is not mindset coaching. This is not meditation tips. This is deep, precise body-based nervous system work — the kind that actually changes how you respond in the moment when it counts.
          </p>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ ...jost, fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: V.sage, marginBottom: '10px' }}>This is for you if</div>
          {[
            'You keep breaking your own trading rules and don\'t know why',
            'You\'ve reset your funded account more times than you want to count',
            'You get close to payout and then something happens',
            'You know what to do and still can\'t seem to do it consistently',
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '7px' }}>
              <span style={{ color: V.sage, fontSize: '13px', lineHeight: 1.7, flexShrink: 0 }}>✓</span>
              <span style={{ ...jost, fontSize: '13px', color: 'rgba(240,237,230,0.75)', lineHeight: 1.7 }}>{item}</span>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <div style={{ ...jost, fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: V.muted, marginBottom: '10px' }}>This is not for you if</div>
          {[
            'You\'re still looking for a better strategy',
            'You\'re not ready to look at what\'s happening beneath the surface',
            'You want quick fixes',
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '6px' }}>
              <span style={{ color: V.dim, fontSize: '12px', lineHeight: 1.7, flexShrink: 0 }}>—</span>
              <span style={{ ...jost, fontSize: '12px', color: V.muted, lineHeight: 1.7 }}>{item}</span>
            </div>
          ))}
        </div>

        <p style={{ ...jost, fontSize: '12px', color: V.muted, marginBottom: '20px' }}>Erin works with clients worldwide via Zoom.</p>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <a
            href="https://link.contentcreatormachine.com/widget/bookings/fdcerin"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...btnGold, fontSize: '13px', padding: '10px 20px', textDecoration: 'none', display: 'inline-block' }}
          >
            Book a Free Consultation →
          </a>
          <a
            href="https://threshold.erindoumert.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...btnGhost, fontSize: '13px', padding: '10px 20px', textDecoration: 'none', display: 'inline-block' }}
          >
            Visit Erin's Website →
          </a>
          <a
            href="mailto:erin@erindoumert.com"
            style={{ ...btnGhost, fontSize: '13px', padding: '10px 20px', textDecoration: 'none', display: 'inline-block' }}
          >
            Email Erin →
          </a>
        </div>
      </div>

      {/* ── Featured Book ── */}
      <div style={{
        background: V.bg2,
        border: `1px solid rgba(201,168,76,0.35)`,
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '14px',
      }}>
        <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '10px', color: V.gold, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px' }}>TRADING PSYCHOLOGY</div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '20px', color: V.gold, marginBottom: '4px' }}>Trading in the Zone</div>
        <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, marginBottom: '16px' }}>Mark Douglas</div>
        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.8, margin: '0 0 16px', whiteSpace: 'pre-line' }}>{`The definational book on trading psychology. Mark Douglas breaks down why traders who understand the markets still fail to make money consistently — and what it actually takes to think in probabilities rather than outcomes.\n\nThis book does not teach strategy. It teaches the mental framework that makes strategy executable.\n\nIf you read one book alongside this program — read this one.`}</p>
        <div style={{ background: V.bg2, borderLeft: '3px solid rgba(201,168,76,0.6)', borderRadius: '6px', padding: '14px', marginTop: '4px' }}>
          <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '16px', color: V.goldL, fontStyle: 'italic', lineHeight: 1.7, margin: 0 }}>"The hard reality of trading is that every trade has an uncertain outcome."</p>
          <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, marginTop: '6px', margin: '6px 0 0' }}>— Mark Douglas</p>
        </div>
      </div>

      {BOOK_SECTIONS.map(({ key, title, intro, items }) => (
        <div key={key} style={{
          marginBottom: '14px',
          borderTop:    `0.5px solid ${V.border}`,
          borderRight:  `0.5px solid ${V.border}`,
          borderBottom: `0.5px solid ${V.border}`,
          borderLeft:   `0.5px solid ${V.border}`,
          borderRadius: '12px', overflow: 'hidden',
        }}>
          <button
            onClick={() => toggle(key)}
            style={{
              width: '100%', padding: '18px 24px',
              background: open[key] ? V.bg2 : V.bg3,
              border: 'none', cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}
          >
            <span style={{ ...serif, fontSize: '19px', color: V.goldL }}>{title}</span>
            <span style={{ color: V.muted, fontSize: '14px' }}>{open[key] ? '▲' : '▼'}</span>
          </button>

          {open[key] && (
            <div style={{ padding: '18px 24px 26px', background: V.bg2 }}>
              {intro && (
                <p style={{ fontSize: '13px', color: V.muted, lineHeight: 1.78, marginBottom: '22px', fontStyle: 'italic', ...jost }}>
                  {intro}
                </p>
              )}
              <div style={{ display: 'grid', gap: '12px' }}>
                {items.map(({ title: t, author, note }) => (
                  <div key={t} style={{
                    padding: '16px 20px', background: V.bg3, borderRadius: '10px',
                    borderTop:    `0.5px solid ${V.border}`,
                    borderRight:  `0.5px solid ${V.border}`,
                    borderBottom: `0.5px solid ${V.border}`,
                    borderLeft:   `3px solid rgba(201,168,76,0.35)`,
                  }}>
                    <div style={{ ...serif, fontSize: '16px', color: V.gold, marginBottom: '4px' }}>{t}</div>
                    <div style={{ fontSize: '11px', color: V.muted, marginBottom: '8px', letterSpacing: '0.04em', ...jost }}>{author}</div>
                    <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.72)', lineHeight: 1.72, fontStyle: 'italic', ...jost }}>{note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────
export default function InnerTrader({ state, onUpdate, onBack, initialTrade, initialTab }) {
  const [activeTab, setActiveTab] = useState(initialTrade ? 'posttrade' : (initialTab || 'intro'));

  return (
    <div style={{ minHeight: '100vh', background: V.bg }}>

      {/* Back */}
      <div style={{ padding: '16px 24px 0' }}>
        <button style={{ ...btnGhost, fontSize: '12px' }} onClick={onBack}>← Dashboard</button>
      </div>

      {/* Tab bar */}
      <div style={{ borderBottom: `0.5px solid ${V.border}`, marginTop: '16px', overflowX: 'auto' }}>
        <div style={{ display: 'flex', padding: '0 16px', gap: '0' }}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                padding: '14px 18px',
                background: 'transparent', border: 'none',
                borderBottom: `2px solid ${activeTab === key ? V.gold : 'transparent'}`,
                color: activeTab === key ? V.goldL : V.muted,
                fontSize: '13px', cursor: 'pointer', ...jost,
                whiteSpace: 'nowrap', transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Sections */}
      {activeTab === 'intro'     && <IntroSection    onNext={() => setActiveTab('parts')} />}
      {activeTab === 'parts'     && <PartsSection     onNext={() => setActiveTab('pretrade')} />}
      {activeTab === 'pretrade'  && <PreTradeSection  state={state} onUpdate={onUpdate} />}
      {activeTab === 'posttrade' && <PostTradeSection state={state} onUpdate={onUpdate} initialTrade={initialTrade} />}
      {activeTab === 'mirror'    && <BeliefMirrorSection state={state} onUpdate={onUpdate} />}
      {activeTab === 'resources' && <ResourcesSection />}
    </div>
  );
}
